import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { getPeriodKey } from "@common/helpers/reports";
import {
    ACCOUNTING_SIDE_LABELS,
    AccountingSide,
} from "@common/types/domains/accounting/accountingSide";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { formatMoneyColumnHeader } from "@common/helpers/number";
import { ColumnGroupConfig, ViewConfig } from "@lib/views";
import { AMOUNT_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import {
    getColumnPinOptions,
    getLabelColumnMaxSize,
} from "../singleColumnReport";
import { TrialBalanceViewRow } from "./trialBalanceViewTypes";
import { makeTrialBalanceDebitCreditCell } from "./TrialBalanceDebitCreditCell";
import { TrialBalanceTitleCell } from "./TrialBalanceTitleCell";

const TB_TITLE_COLUMN_ID = "title";
const TRIAL_BALANCE_SIDES = [AccountingSide.DEBIT, AccountingSide.CREDIT];
const TITLE_MIN_SIZE = 320;
const TITLE_MAX_SIZE = 1200;

export function buildTrialBalanceConfig(
    periods: ReportPeriodWithLabel[],
    numberFormat?: NumberFormatValues,
): ViewConfig<TrialBalanceViewRow, string, never> {
    const columns: Record<
        string,
        ViewConfig<TrialBalanceViewRow, string, never>["columns"][string]
    > = {
        [TB_TITLE_COLUMN_ID]: {
            name: "Account",
            header: "",
            accessorKey: "title",
            cell: TrialBalanceTitleCell,
            minSize: TITLE_MIN_SIZE,
            maxSize: getLabelColumnMaxSize({
                periods,
                labelMaxSize: TITLE_MAX_SIZE,
            }),
            immutable: true,
        },
    };

    const groups: Record<string, ColumnGroupConfig> = {};

    periods.forEach((period, periodIndex) => {
        const periodKey = getPeriodKey(period);
        const groupId = `period-${periodKey}`;
        groups[groupId] = { header: period.label };

        TRIAL_BALANCE_SIDES.forEach((side) => {
            columns[`${groupId}-${side}`] = {
                name: `${period.label} ${ACCOUNTING_SIDE_LABELS[side]}`,
                header: formatMoneyColumnHeader(
                    ACCOUNTING_SIDE_LABELS[side],
                    numberFormat,
                ),
                accessorKey: "values",
                cell: makeTrialBalanceDebitCreditCell({
                    period,
                    periodIndex,
                    kind: side,
                    firstPeriod: periods[0],
                }),
                ...AMOUNT_COLUMN_SIZE,
                groupId,
            };
        });
    });

    return { columns, groups };
}

export function buildTrialBalanceView(periods: ReportPeriodWithLabel[]): View {
    return {
        type: ViewType.TRIAL_BALANCE,
        columns: [
            {
                id: TB_TITLE_COLUMN_ID,
                options: getColumnPinOptions({ periods, pin: "left" }),
            },
            ...periods.flatMap((p) => {
                const groupId = `period-${getPeriodKey(p)}`;
                return TRIAL_BALANCE_SIDES.map((side) => ({
                    id: `${groupId}-${side}`,
                }));
            }),
        ],
        filters: {},
        options: {
            selection: false,
            bulkActions: false,
            density: ViewDensity.LG,
        },
    };
}
